import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import BaseTable from "@reusables/BaseTable";
import { selectPaymentTermsById, useGetPaymentTermsQuery } from "@redux/features/paymentTerms/paymentTermsApi";

import { ReactComponent as EditSVG } from "@assets/icons/ic_edit.svg";
import { ReactComponent as DeleteSVG } from "@assets/icons/ic_trash.svg";
import BaseMaterialButton from "@reusables/BaseMaterialButton";
import MutationModal from "./modals/MutationModal";
import { useAppSelector } from "@redux/hooks";
import DeletionModal from "./modals/DeletionModal";
import { AbilityContext, Can } from "@/casl.config";
import { useAbility } from "@casl/react";
import NoPermissionBanner from "@/components/ErrorPages/NoPermissionBanner";
import BaseBadge from "@reusables/BaseBadge";
import { Star } from "lucide-react";
import { usePaymentTermTypeOptions } from "./helpers";

export default function PaymentTermsPage() {

    const { t } = useTranslation("", { keyPrefix: "settings.general.paymentTerms" });

    const ability = useAbility(AbilityContext);

    const { getLabel: getPaymentTypeLabel } = usePaymentTermTypeOptions();

    // Getting payment terms data using RTK Query
    const { data: paymentTerms = [], isLoading: isLoadingPaymentTerms } = useGetPaymentTermsQuery();

    // Open states determine visibility of modals
    const [isMutationModalOpen, setIsMutationModalOpen] = useState(false);
    const [isDeletionModalOpen, setIsDeletionModalOpen] = useState(false);

    // Ids and corresponding selected cache entries allows modals to work with data
    const [selectedPaymentTermsId, setSelectedPaymentTermsId] = useState<number>();
    const [deletablePaymentTermsId, setDeletablePaymentTermsId] = useState<number>();

    const selectedPaymentTerms = useAppSelector(state => selectPaymentTermsById(state, selectedPaymentTermsId));
    const deletablePaymentTerms = useAppSelector(state => selectPaymentTermsById(state, deletablePaymentTermsId));

    return (
        <>
            <div className="levitation-extended">
                <div className="flex flex-row justify-between items-center mb-[44px]">
                    <div className="font-bold text-2xl text-accent">{t("heading")}</div>
                    <Can I="payment_terms.edit" a="general">
                        <BaseMaterialButton fontWeight={500} onClick={() => {
                            setSelectedPaymentTermsId(undefined);
                            setIsMutationModalOpen(true);
                        }}>
                            {t("table.buttons.add")}
                        </BaseMaterialButton>
                    </Can>
                </div>

                <Can not I="payment_terms.view" a="general">
                    <NoPermissionBanner />
                </Can>

                <Can I="payment_terms.view" a="general">
                    <BaseTable
                        data={paymentTerms}
                        columns={[
                            {
                                header: t("table.columns.0"),
                                getter: (row) => (
                                    <div className="flex items-center space-x-2">
                                        <span className="bold-highlight">{row.name}</span>
                                        {row.is_default && (
                                            <BaseBadge icon={<Star size={12} />}>
                                                {t("table.defaultBadge")}
                                            </BaseBadge>
                                        )}
                                    </div>
                                ),
                                comparator: (a, b) => a.name.localeCompare(b.name)
                            },
                            {
                                header: t("table.columns.1"),
                                getter: (row) => row.days,
                                comparator: (a, b) => a.days - b.days
                            },
                            {
                                header: t("table.columns.2"),
                                getter: (row) => getPaymentTypeLabel(row.type),
                                comparator: (a, b) => getPaymentTypeLabel(a.type).localeCompare(getPaymentTypeLabel(b.type))
                            },
                            {
                                visible: ability.can("payment_terms.edit", "general"),
                                header: () => "",
                                getter: (row) => (
                                    <div className="text-tables-secondaryIcon">
                                        <Can I="payment_terms.edit" a="general">
                                            <EditSVG
                                                className="mr-[8px] cursor-pointer hover:text-tables-highlightedIcon"
                                                onClick={() => {
                                                    setSelectedPaymentTermsId(row.id);
                                                    setIsMutationModalOpen(true);
                                                }}
                                            />
                                        </Can>
                                        <Can I="payment_terms.edit" a="general">
                                            <DeleteSVG
                                                className="cursor-pointer hover:text-tables-highlightedIcon"
                                                onClick={() => {
                                                    setDeletablePaymentTermsId(row.id);
                                                    setIsDeletionModalOpen(true);
                                                }}
                                            />
                                        </Can>
                                    </div>
                                ),
                                sx: {
                                    textAlign: "right"
                                }
                            }
                        ]}
                        isDataLoading={isLoadingPaymentTerms}

                        headerSx={{
                            backgroundColor: "#F9F9F9"
                        }}

                        sx={{
                            borderCollapse: "separate",
                            borderSpacing: "0 15px"
                        }}

                        size={"small"}
                    />
                </Can>
            </div>

            <MutationModal
                isOpen={isMutationModalOpen}
                onClose={() => {
                    setIsMutationModalOpen(false);
                }}
                paymentTerms={selectedPaymentTerms}
            />

            <DeletionModal
                isOpen={isDeletionModalOpen}
                onClose={() => setIsDeletionModalOpen(false)}
                paymentTerms={deletablePaymentTerms}
            />
        </>
    );
}